/**
 * The `debounceWithImmediate` function is a TypeScript implementation of a debounce function that
 * allows for an immediate execution of the function if specified.
 * @param func - The `func` parameter is a function that you want to debounce. It is a function that
 * takes no arguments and returns nothing (`void`).
 * @param {number} delay - The `delay` parameter is the amount of time in milliseconds that the
 * function should wait before executing after the last invocation.
 * @param {boolean} [immediate] - The `immediate` parameter is an optional boolean value that
 * determines whether the `func` should be called immediately or after the delay. If `immediate` is
 * `true` and there is no existing timeout, the `func` will be called immediately. If `immediate` is
 * `false
 * @returns The function `debounceWithImmediate` returns a new function that can be invoked later.
 */
export function debounce(func: () => void, delay: number, immediate?: boolean): Function {
  let timeoutId: number | undefined | ReturnType<typeof setTimeout>

  return (...args: []) => {
    const shouldCallImmediate = immediate && timeoutId === undefined

    if (timeoutId) {
      clearTimeout(timeoutId)
    }

    if (shouldCallImmediate) {
      func(...args)
    }

    timeoutId = setTimeout(() => {
      if (!shouldCallImmediate) {
        func(...args)
      }
    }, delay)
  }
}
